import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Zap, Brain, Upload, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';
import { COACH_ADDON } from '@shared/constants/tiers';

export type PaywallTrigger = 'tutor_limit' | 'upload_limit' | 'diagnostic_upgrade' | 'feature_gate' | 'coach_addon';

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  trigger: PaywallTrigger;
  currentTier: string;
  targetTier?: string;
}

const triggerContent: Record<PaywallTrigger, {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaLabel: string;
}> = {
  tutor_limit: {
    icon: <Brain size={32} className="text-indigo-400" />,
    title: 'Tutor queries used up',
    description: 'Upgrade to keep learning with your AI Tutor. Pro gives you unlimited queries — ask anything, anytime.',
    ctaLabel: 'Upgrade for Unlimited Tutor',
  },
  upload_limit: {
    icon: <Upload size={32} className="text-indigo-400" />,
    title: 'Upload limit reached',
    description: 'You\'ve used your free upload. Upgrade to upload more content and build personalised learning paths for every subject.',
    ctaLabel: 'Upgrade to Upload More',
  },
  diagnostic_upgrade: {
    icon: <Zap size={32} className="text-gold-400" />,
    title: 'Unlock your full CogniPrint',
    description: 'You\'ve seen 4 dimensions of your learning profile. Upgrade to unlock all 8 dimensions and get the complete picture of how you learn.',
    ctaLabel: 'Unlock Full Profile',
  },
  feature_gate: {
    icon: <Lock size={32} className="text-indigo-400" />,
    title: 'Premium feature',
    description: 'This feature is available on paid plans. Upgrade to access the full CogniPrint experience.',
    ctaLabel: 'View Plans',
  },
  coach_addon: {
    icon: <Sparkles size={32} className="text-gold-400" />,
    title: `Add Coach — $${COACH_ADDON.monthlyPrice}/mo`,
    description:
      'Bi-weekly recalibration, unlimited ad-hoc check-ins, Opus routing, the counterfactual simulator, and auto-pilot suggestions. Stacks on your current plan.',
    ctaLabel: 'Add Coach',
  },
};

export function PaywallModal({ open, onClose, trigger, currentTier, targetTier }: PaywallModalProps) {
  const navigate = useNavigate();
  const content = triggerContent[trigger];

  // Track paywall shown
  useEffect(() => {
    if (open) {
      trackEvent({
        name: ANALYTICS_EVENTS.SUBSCRIPTION_PAYWALL_SHOWN,
        properties: {
          ...getBaseProperties(),
          tier: currentTier as 'free' | 'learner' | 'pro' | 'teams',
          trigger,
          currentTier,
          targetTier: targetTier ?? null,
        },
      });
    }
  }, [open, trigger, currentTier, targetTier]);

  function handleUpgrade() {
    trackEvent({
      name: ANALYTICS_EVENTS.SUBSCRIPTION_UPGRADE_STARTED,
      properties: {
        ...getBaseProperties(),
        tier: currentTier as 'free' | 'learner' | 'pro' | 'teams',
        fromTier: currentTier,
        toTier: targetTier ?? (trigger === 'coach_addon' ? 'coach' : 'pro'),
        trigger,
      },
    });
    onClose();
    navigate(trigger === 'coach_addon' ? '/pricing?addon=coach' : '/pricing');
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="bg-dark-surface border border-dark-border rounded-lg p-6 max-w-sm w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute top-3 right-3 text-content-muted hover:text-content transition-colors"
              >
                <X size={20} />
              </button>

              <div className="text-center">
                <div className="mb-4 flex justify-center">{content.icon}</div>
                <h3 className="font-heading font-bold text-xl mb-2">{content.title}</h3>
                <p className="text-content-secondary font-body text-sm mb-6 leading-relaxed">
                  {content.description}
                </p>

                <div className="space-y-3">
                  <Button className="w-full" size="lg" onClick={handleUpgrade}>
                    {content.ctaLabel}
                  </Button>
                  <button
                    onClick={onClose}
                    className="text-content-muted text-sm font-body hover:text-content transition-colors"
                  >
                    Not now
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
